import { defineNuxtRouteMiddleware } from '#app';
import { isBundledNativeOrigin, isNativeApp } from '~/functions/appOrigin';
import { isAppOriginatedPaymentReturn, nativeReturnUrl } from '~/functions/nativePaymentReturn';

/**
 * Hands an app buyer back to the app when Stripe returns them to the **website**.
 *
 * The API marks the URLs it gives Stripe with `client=capacitor-ios` when the checkout was
 * created by the packaged app. `/payment/success` reads that marker itself and bounces — but
 * the **cancel** URL is `{requester}{cancelUrl}`, and `cancelUrl` is whichever page the
 * purchase started from: `/lms/membership/{slug}`, `/lms/courses/{slug}`,
 * `/lms/premium/{slug}`, `/lms/premium-subscriptions/{slug}`, and whatever the next
 * purchasable page type turns out to be.
 *
 * So there is no cancel landing route to give bespoke handling to. Doing it per page would
 * mean touching every product page and silently losing the bounce on the next one added.
 * This catches all of them in one place.
 *
 * ## Why it must run before the auth middleware
 *
 * The `002-` prefix is load-bearing. Global middleware runs in filename order, so this sits
 * after `001-portal-redirect` and **before `auth.global`** — which matters because the cancel
 * URL lands on an `/lms` route, and the buyer has no session in that browser. Left to run
 * first, the auth middleware would redirect them to a login page for a purchase they just
 * abandoned, and the marker would be lost with the redirect.
 */
export default defineNuxtRouteMiddleware((to) => {
   if (import.meta.server) return;

   // The packaged app runs this same build. Bouncing there would send the app to itself,
   // and every navigation carrying a stray `client=` would do it again.
   if (isNativeApp() && isBundledNativeOrigin()) return;

   // Handles the marker itself, and renders a visible "Return to the app" link that a bare
   // redirect cannot. Two mechanisms racing on one page is worse than one.
   if (to.path === '/payment/success') return;

   if (!isAppOriginatedPaymentReturn(to.query.client)) return;

   window.location.replace(nativeReturnUrl(withoutClientMarker(to.fullPath)));
});

/**
 * The marker is what tells the *website* to bounce. Carrying it into the app route would put
 * it back in the query string of a page the app then renders — harmless today, since the
 * guard above stops the app acting on it, but it invites a loop the first time that guard is
 * relaxed.
 */
function withoutClientMarker(fullPath: string): string {
   const [path, query] = fullPath.split('?');

   if (!query) return path;

   const params = new URLSearchParams(query);

   params.delete('client');

   const remaining = params.toString();

   return remaining ? `${path}?${remaining}` : path;
}
